import React, { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Play, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import Editor from '@monaco-editor/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Toast from '../../Toast';
import toolsService from '../../../services/toolsService';
import { CreateToolPageStyle } from './CreateToolModal.style';

const toolTypes = ['API', 'CODE'];
const httpMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const parameterTypes = ['String', 'Number', 'Boolean', 'Array', 'Object'];
const languageOptions = [{ label: 'Python', value: 'python' }];
const languageMap = {
  javascript: 63,
  typescript: 74,
  python: 71,
  c: 50,
  cpp: 54,
  java: 62,
  php: 68,
};

let uid = 0;
const makeId = () => `row_${Date.now()}_${uid++}`;
const emptyDef = () => ({ id: makeId(), name: '', type: '', desc: '', defaultValue: '', required: 'false' });

function Section({ title, description, action, children }) {
  return (
    <div className="section">
      <div className="section-head">
        <div>
          <h4>{title}</h4>
          {description ? <p>{description}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="field">
      <label className="label">
        {required ? <span className="req">*</span> : null} {label}
      </label>
      {children}
    </div>
  );
}

function AddRowButton({ onClick, label }) {
  return (
    <button type="button" className="add-btn" onClick={onClick}>
      <Plus size={14} /> {label}
    </button>
  );
}

function DefinitionList({ label, items, setItems }) {
  const update = (id, field, value) => {
    setItems(items.map(item => (item.id === id ? { ...item, [field]: value } : item)));
  };

  return (
    <div className="stack">
      {items.map((item, index) => (
        <div key={item.id} className="def-card">
          <div className="def-head">
            <span>
              {label} {index + 1}
            </span>
            <button type="button" className="icon-btn" onClick={() => setItems(items.filter(i => i.id !== item.id))}>
              <Trash2 size={16} />
            </button>
          </div>
          <div className="grid-2">
            <Field label={`${label} Name`}>
              <input
                className="input"
                value={item.name}
                onChange={e => update(item.id, 'name', e.target.value)}
                placeholder={`Enter ${label.toLowerCase()} name`}
              />
            </Field>
            <Field label="Type">
              <select className="select" value={item.type} onChange={e => update(item.id, 'type', e.target.value)}>
                <option value="">Select type</option>
                {parameterTypes.map(t => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Default Value">
              <input
                className="input"
                value={item.defaultValue}
                onChange={e => update(item.id, 'defaultValue', e.target.value)}
                placeholder="Enter default value"
              />
            </Field>
            <Field label="Required">
              <select
                className="select"
                value={item.required}
                onChange={e => update(item.id, 'required', e.target.value)}>
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            </Field>
          </div>
          <Field label="Description">
            <input
              className="input"
              value={item.desc}
              onChange={e => update(item.id, 'desc', e.target.value)}
              placeholder={`Describe this ${label.toLowerCase()}...`}
            />
          </Field>
        </div>
      ))}
    </div>
  );
}

function validateAndConvert(type, rawValue) {
  switch (type) {
    case 'Number':
      if (Number.isNaN(Number(rawValue))) return { valid: false };
      return { valid: true, value: Number(rawValue) };
    case 'Boolean': {
      const normalized = String(rawValue).toLowerCase();
      if (!['true', 'false'].includes(normalized)) return { valid: false };
      return { valid: true, value: normalized === 'true' };
    }
    case 'Array':
      try {
        const parsed = JSON.parse(rawValue);
        if (!Array.isArray(parsed)) return { valid: false };
        return { valid: true, value: parsed };
      } catch {
        return { valid: false };
      }
    case 'Object':
      try {
        const parsed = JSON.parse(rawValue);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return { valid: false };
        return { valid: true, value: parsed };
      } catch {
        return { valid: false };
      }
    case 'String':
    default:
      return { valid: true, value: rawValue };
  }
}

function buildHeadersObject(headers) {
  return headers.reduce((acc, h) => {
    if (h.key && h.value) {
      let uniqueKey = h.key;
      let counter = 2;
      while (acc[uniqueKey]) {
        uniqueKey = `${h.key}${counter}`;
        counter++;
      }
      acc[uniqueKey] = h.value;
    }
    return acc;
  }, {});
}

const buildDefList = items =>
  items
    .filter(item => item.name && item.type)
    .map(item => ({
      name: item.name,
      desc: item.desc,
      required: item.required,
      type: item.type,
      default_value: item.defaultValue || null,
    }));

const CreateToolPage = ({ toolId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!toolId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [toolType, setToolType] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [method, setMethod] = useState('');
  const [headers, setHeaders] = useState([]);
  const [params, setParams] = useState([]);
  const [body, setBody] = useState([]);

  const [language, setLanguage] = useState('python');
  const [code, setCode] = useState('');
  const [running, setRunning] = useState(false);
  const [executionOutput, setExecutionOutput] = useState('');

  const [testSnapshot, setTestSnapshot] = useState(null);
  const [testValues, setTestValues] = useState({});
  const [testing, setTesting] = useState(false);
  const [testOutput, setTestOutput] = useState(null);

  const [detailLoading, setDetailLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return undefined;
    (async () => {
      try {
        setDetailLoading(true);
        const resp = await toolsService.getToolDetails(toolId);
        const detail = resp?.detail;
        if (!detail) return;
        setName(detail.tool_name || '');
        setDescription(detail.description || '');
        setToolType(detail.tool_category || '');
        setEndpoint(detail.url || '');
        setMethod(detail.method || '');
        setHeaders(Object.entries(detail.headers || {}).map(([key, value]) => ({ id: makeId(), key, value })));
        setParams(
          (detail.params || []).map(p => ({
            id: makeId(),
            name: p.name || '',
            type: p.type || '',
            desc: p.desc || '',
            defaultValue: p.default_value ?? '',
            required: String(p.required ?? 'false'),
          })),
        );
        setBody(
          (detail.body || []).map(b => ({
            id: makeId(),
            name: b.name || '',
            type: b.type || '',
            desc: b.desc || '',
            defaultValue: b.default_value ?? '',
            required: String(b.required ?? 'false'),
          })),
        );
      } catch (error) {
        console.log(error);
      } finally {
        setDetailLoading(false);
      }
    })();
  }, [toolId]);

  const saveMutation = useMutation({
    mutationFn: async payload => {
      if (isEdit) return toolsService.updateTool(payload);
      return toolsService.createTool(payload);
    },
    onSuccess: () => {
      Toast({ type: 'success', message: isEdit ? 'Tool updated successfully' : 'Tool created successfully' });
      queryClient.invalidateQueries({ queryKey: ['list'] });
      navigate('/tools');
    },
    onError: err => {
      Toast({ type: 'error', message: err?.detail || err?.message || 'Something went wrong' });
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      Toast({ type: 'error', message: 'Tool name is required' });
      return;
    }
    if (!toolType) {
      Toast({ type: 'error', message: 'Tool Type is required' });
      return;
    }
    if (!description.trim()) {
      Toast({ type: 'error', message: 'Description is required' });
      return;
    }

    saveMutation.mutate({
      ...(toolId ? { tool_id: toolId } : {}),
      tool_name: name,
      description,
      tool_category: toolType,
      tool_type: 'user_created',
      url: endpoint,
      method,
      headers: buildHeadersObject(headers),
      params: buildDefList(params),
      body: buildDefList(body),
    });
  };

  const handleFetchTestData = () => {
    setTestSnapshot({ endpoint, method, headers: [...headers], params: [...params], body: [...body] });
    const values = {};
    params.forEach(p => {
      values[`param_${p.id}`] = p.defaultValue ?? '';
    });
    body.forEach(b => {
      values[`body_${b.id}`] = b.defaultValue ?? '';
    });
    setTestValues(values);
  };

  const handleTest = async () => {
    if (!testSnapshot) {
      Toast({ type: 'info', message: 'Fetch data before testing the tool' });
      return;
    }
    setTesting(true);

    const paramsObj = {};
    for (const p of testSnapshot.params) {
      const raw = testValues[`param_${p.id}`];
      if (!p.name || raw === undefined || raw === '') continue;
      const { valid, value } = validateAndConvert(p.type, raw);
      if (!valid) {
        Toast({ type: 'error', message: `Invalid type for parameter "${p.name}". Expected ${p.type}.` });
        continue;
      }
      let uniqueKey = p.name;
      let counter = 2;
      while (uniqueKey in paramsObj) {
        uniqueKey = `${p.name}${counter}`;
        counter++;
      }
      paramsObj[uniqueKey] = value;
    }

    const bodyObj = {};
    for (const b of testSnapshot.body) {
      const raw = testValues[`body_${b.id}`];
      if (!b.name || raw === undefined || raw === '') continue;
      const { valid, value } = validateAndConvert(b.type, raw);
      if (!valid) {
        Toast({ type: 'error', message: `Invalid type for body parameter "${b.name}". Expected ${b.type}.` });
        continue;
      }
      let uniqueKey = b.name;
      let counter = 2;
      while (uniqueKey in bodyObj) {
        uniqueKey = `${b.name}${counter}`;
        counter++;
      }
      bodyObj[uniqueKey] = value;
    }

    try {
      const res = await toolsService.testTool({
        tool_name: name,
        description,
        tool_category: toolType,
        tool_type: 'user_created',
        url: testSnapshot.endpoint,
        method: testSnapshot.method,
        headers: buildHeadersObject(testSnapshot.headers),
        params: paramsObj,
        body: bodyObj,
      });
      setTestOutput(res);
    } catch (error) {
      Toast({ type: 'error', message: error.message });
      setTestOutput(error);
    } finally {
      setTesting(false);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setExecutionOutput('Running your code...');
    try {
      const response = await fetch('https://ce.judge0.com/submissions?base64_encoded=false&wait=true', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          language_id: languageMap[language],
          source_code: code,
          stdin: '',
        }),
      });
      const result = await response.json();
      if (result.stderr) setExecutionOutput(result.stderr);
      else if (result.compile_output) setExecutionOutput(result.compile_output);
      else setExecutionOutput(result.stdout || 'No output');
    } catch (error) {
      setExecutionOutput(`Error: ${error.message}`);
    }
    setRunning(false);
  };

  const saving = saveMutation.isPending;

  return (
    <CreateToolPageStyle>
      <button type="button" className="back-btn" onClick={() => navigate('/tools')}>
        <ArrowLeft size={16} /> Back to Tools
      </button>

      <div className="page-header">
        <h1>{isEdit ? 'Edit Tool' : 'Create Tool'}</h1>
        <p>Define a tool with connection parameters so agents can call it during workflows.</p>
      </div>

      {detailLoading ? (
          <>
            <div className="loading-line">
              <Loader2 className="spin" size={16} /> Loading tool details...
            </div>
            <div className="skeleton-stack">
              {[0, 1, 2].map(i => (
                <div key={i} className="skeleton" />
              ))}
            </div>
          </>
        ) : (
          <div className="sections">
            <Section title="Basics">
              <div className="stack">
                <div className="grid-2">
                  <Field label="Tool Name" required>
                    <input
                      className="input"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Name your tool"
                    />
                  </Field>
                  <Field label="Tool Type" required>
                    <select className="select" value={toolType} onChange={e => setToolType(e.target.value)}>
                      <option value="">Select tool type</option>
                      {toolTypes.map(t => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Description" required>
                  <textarea
                    className="textarea"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={3}
                    placeholder="What does this tool do and when should an agent use it?"
                  />
                </Field>
              </div>
            </Section>

            {toolType === 'API' && (
              <>
                <Section title="API Configuration" description="Configure the API endpoint and request method">
                  <div className="grid-4">
                    <Field label="API Endpoint">
                      <input
                        className="input"
                        value={endpoint}
                        onChange={e => setEndpoint(e.target.value)}
                        placeholder="https://api.example.com/endpoint"
                      />
                    </Field>
                    <Field label="HTTP Method">
                      <select className="select" value={method} onChange={e => setMethod(e.target.value)}>
                        <option value="">Select method</option>
                        {httpMethods.map(m => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </Section>

                <Section
                  title="Request Headers"
                  description="Custom headers to include with the request (e.g. Content-Type)."
                  action={
                    <AddRowButton
                      onClick={() => setHeaders([...headers, { id: makeId(), key: '', value: '' }])}
                      label="Add header"
                    />
                  }>
                  {headers.length === 0 ? (
                    <p className="empty-note">No headers added.</p>
                  ) : (
                    <div className="stack">
                      {headers.map(h => (
                        <div key={h.id} className="header-row">
                          <input
                            className="input"
                            value={h.key}
                            onChange={e =>
                              setHeaders(headers.map(x => (x.id === h.id ? { ...x, key: e.target.value } : x)))
                            }
                            placeholder="Authorization"
                          />
                          <input
                            className="input"
                            value={h.value}
                            onChange={e =>
                              setHeaders(headers.map(x => (x.id === h.id ? { ...x, value: e.target.value } : x)))
                            }
                            placeholder="Bearer token 123"
                          />
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => setHeaders(headers.filter(x => x.id !== h.id))}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>

                <Section
                  title="Parameters"
                  description="Define the parameters that this tool accepts"
                  action={<AddRowButton onClick={() => setParams([...params, emptyDef()])} label="Add parameter" />}>
                  {params.length === 0 ? (
                    <p className="empty-note">No parameters added.</p>
                  ) : (
                    <DefinitionList label="Parameter" items={params} setItems={setParams} />
                  )}
                </Section>

                <Section
                  title="Body"
                  description="Define the body that this tool accepts"
                  action={<AddRowButton onClick={() => setBody([...body, emptyDef()])} label="Add body field" />}>
                  {body.length === 0 ? (
                    <p className="empty-note">No body fields added.</p>
                  ) : (
                    <DefinitionList label="Body" items={body} setItems={setBody} />
                  )}
                </Section>

                <Section
                  title="Test Tool"
                  description="Make sure to fill all the fields and fetch data before testing tool"
                  action={
                    <button type="button" className="add-btn" onClick={handleFetchTestData}>
                      <RefreshCw size={14} /> Fetch Data
                    </button>
                  }>
                  <div className="stack">
                    <div className="meta-grid">
                      <div className="meta-box">
                        <p className="k">API Endpoint</p>
                        <p className="v">{testSnapshot?.endpoint || 'Null'}</p>
                      </div>
                      <div className="meta-box">
                        <p className="k">HTTP Method</p>
                        <p className="v">{testSnapshot?.method || 'Null'}</p>
                      </div>
                    </div>

                    {testSnapshot?.headers?.length > 0 && (
                      <div>
                        <p className="label" style={{ marginBottom: 8 }}>
                          Headers
                        </p>
                        <div className="stack">
                          {testSnapshot.headers.map(h => (
                            <p key={h.id} style={{ margin: 0, fontSize: 14, color: '#fff' }}>
                              <span style={{ color: '#808080' }}>{h.key || 'Null'}:</span> {h.value || 'Null'}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {testSnapshot?.params?.length > 0 && (
                      <div className="stack">
                        <p className="label">Parameters</p>
                        <div className="grid-2">
                          {testSnapshot.params.map(p => (
                            <Field key={p.id} label={p.name || 'Unnamed parameter'}>
                              <input
                                className="input"
                                value={testValues[`param_${p.id}`] ?? ''}
                                onChange={e => setTestValues(v => ({ ...v, [`param_${p.id}`]: e.target.value }))}
                                placeholder="Enter value to proceed"
                              />
                            </Field>
                          ))}
                        </div>
                      </div>
                    )}

                    {testSnapshot?.body?.length > 0 && (
                      <div className="stack">
                        <p className="label">Body</p>
                        <div className="grid-2">
                          {testSnapshot.body.map(b => (
                            <Field key={b.id} label={b.name || 'Unnamed field'}>
                              <input
                                className="input"
                                value={testValues[`body_${b.id}`] ?? ''}
                                onChange={e => setTestValues(v => ({ ...v, [`body_${b.id}`]: e.target.value }))}
                                placeholder="Enter value to proceed"
                              />
                            </Field>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="stack">
                      <p className="label">Output</p>
                      <pre className="output">
                        {testOutput
                          ? JSON.stringify(testOutput?.data ?? testOutput, null, 2)
                          : 'Fetch data and run a test to see output.'}
                      </pre>
                    </div>

                    <div className="section-actions">
                      <button type="button" className="primary" onClick={handleTest} disabled={testing}>
                        {testing ? <Loader2 className="spin" size={16} /> : <Play size={16} />}
                        Test Tool
                      </button>
                    </div>
                  </div>
                </Section>
              </>
            )}

            {toolType === 'CODE' && (
              <Section title="Code" description="Write the code this tool runs">
                <div className="stack">
                  <Field label="Language">
                    <select className="select" value={language} onChange={e => setLanguage(e.target.value)}>
                      {languageOptions.map(l => (
                        <option key={l.value} value={l.value}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="editor-wrap">
                    <Editor
                      height="250px"
                      theme="vs-dark"
                      language={language}
                      value={code}
                      onChange={value => setCode(value)}
                      options={{
                        lineNumbers: 'on',
                        minimap: { enabled: false },
                        fontSize: 14,
                        lineHeight: 22,
                        padding: { top: 16, bottom: 16 },
                        smoothScrolling: true,
                        stickyScroll: { enabled: false },
                      }}
                    />
                  </div>
                  <div className="stack">
                    <p className="label">Output</p>
                    <pre className="output">{executionOutput || 'Run the code to see output.'}</pre>
                  </div>
                  <div className="section-actions">
                    <button type="button" className="primary" onClick={handleRun} disabled={running}>
                      {running ? <Loader2 className="spin" size={16} /> : <Play size={16} />}
                      Test Code
                    </button>
                  </div>
                </div>
              </Section>
            )}
          </div>
        )}

      <div className="actions">
        <button type="button" className="cancel" onClick={() => navigate('/tools')}>
          Cancel
        </button>
        <button type="button" className="primary" onClick={handleSave} disabled={saving || detailLoading}>
          {saving ? <Loader2 className="spin" size={16} /> : <Save size={16} />}
          {saving ? 'Saving...' : isEdit ? 'Update Tool' : 'Create Tool'}
        </button>
      </div>
    </CreateToolPageStyle>
  );
};

export default CreateToolPage;
